/**
 * 消息列表（普通文档流 + 滚动）
 * 历史记录由 AiChatModal 在挂载时 replaceMessages，避免重复追加
 */

import React, { useRef, useEffect } from 'react';
import { useChatStore, useServerStore } from '@/stores';
import { useIsLightUi } from '@/hooks';
import { messagesInline } from '@/utils/themeInline';
import { useThrottle } from '@/hooks';
import { saveMessages } from '@/utils/storage';
import { MessageItem } from './MessageItem';
import { QuickCommands } from './QuickCommands';
import { AgentLivePanel } from './AgentLivePanel';
import type { Message } from '@/types';

interface MessageListProps {
  /** 用户编辑历史消息后：截断后续并重新发送 */
  onUserMessageResend?: (messageId: string, newContent: string) => void;
  /** 欢迎页快捷命令：与底部输入区快捷操作一致，发送预设文案 */
  onQuickCommand?: (command: string) => void;
  /** IndexedDB 恢复完成前禁用快捷命令，避免竞态 */
  storageReady?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({
  onUserMessageResend,
  onQuickCommand,
  storageReady = true,
}) => {
  const messages = useChatStore((s) => s.messages);
  const isProcessing = useChatStore((s) => s.isProcessing);
  const currentThinking = useChatStore((s) => s.currentThinking);
  const liveStatus = useChatStore((s) => s.liveStatus);
  const { activeServerId } = useServerStore();
  const isLightUi = useIsLightUi();
  const scrollRef = useRef<HTMLDivElement>(null);

  const throttledSave = useThrottle(
    (...args: unknown[]) => {
      const serverId = args[0] as number;
      const msgs = args[1] as Message[];
      return saveMessages(serverId, msgs);
    },
    2000
  );

  useEffect(() => {
    if (activeServerId) {
      throttledSave(activeServerId, messages);
    }
  }, [messages, activeServerId, throttledSave]);

  const scrollToBottom = useThrottle(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, 100);

  useEffect(() => {
    scrollToBottom();
  }, [
    messages.length,
    messages,
    isProcessing,
    currentThinking,
    liveStatus,
    scrollToBottom,
  ]);

  const showLivePanel =
    isProcessing ||
    Boolean(currentThinking?.trim()) ||
    Boolean(liveStatus?.trim());

  return (
    <div
      className="sse-ai-messages"
      ref={scrollRef}
      style={messagesInline(isLightUi)}
    >
      {messages.length === 0 ? (
        <QuickCommands onCommand={onQuickCommand} storageReady={storageReady} />
      ) : null}
      {messages.length > 0
        ? messages.map((msg: Message) => (
            <MessageItem
              key={msg.id}
              message={msg}
              onUserMessageResend={onUserMessageResend}
            />
          ))
        : null}
      {showLivePanel ? <AgentLivePanel /> : null}
    </div>
  );
};
